import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import { Navbar } from '../components/Navbar';
import { PostCard, WORKFLOW_COLORS, WORKFLOW_LABELS } from '../components/PostCard';
import { PostModal } from '../components/PostModal';
import { PostPreview } from '../components/PostPreview';
import { postsApi, authFetch } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import { ArrowLeft, Plus, Calendar, List, Grid, Copy, FileDown, GripVertical, Send, Trash2, CheckSquare, Square, Check, ChevronLeft, ChevronRight, ExternalLink, Link2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function MonthDetailPage({ readOnly = false }) {
  const { year, month } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeClient } = useClient();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [duplicatePost, setDuplicatePost] = useState(null);
  const [duplicateDate, setDuplicateDate] = useState('');
  const [previewPost, setPreviewPost] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const { toast } = useToast();

  const isClientView = readOnly || location.pathname.startsWith('/client');
  const yearNum = parseInt(year);
  const monthNum = parseInt(month);
  const monthName = MONTH_NAMES[monthNum - 1];
  const monthDate = new Date(yearNum, monthNum - 1, 1);
  const backPath = isClientView ? '/client' : '/dashboard';

  // Prev / Next month navigation
  const prevMonth = monthNum === 1 ? { m: 12, y: yearNum - 1 } : { m: monthNum - 1, y: yearNum };
  const nextMonth = monthNum === 12 ? { m: 1, y: yearNum + 1 } : { m: monthNum + 1, y: yearNum };
  const prevMonthName = MONTH_NAMES[prevMonth.m - 1];
  const nextMonthName = MONTH_NAMES[nextMonth.m - 1];
  const basePath = isClientView ? '/client/month' : '/month';
  const goToPrevMonth = () => navigate(`${basePath}/${prevMonth.y}/${prevMonth.m}`);
  const goToNextMonth = () => navigate(`${basePath}/${nextMonth.y}/${nextMonth.m}`);

  // Adjacent month post counts
  const [adjCounts, setAdjCounts] = useState({});
  useEffect(() => {
    const prevKey = `${prevMonth.y}-${String(prevMonth.m).padStart(2, '0')}`;
    const nextKey = `${nextMonth.y}-${String(nextMonth.m).padStart(2, '0')}`;
    const slug = localStorage.getItem('activeClientSlug') || '';
    authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/month-counts?months=${prevKey},${nextKey}`, {
      headers: { 'X-Client-Id': slug }
    })
      .then(r => r.json())
      .then(data => setAdjCounts(data))
      .catch(() => {});
  }, [monthNum, yearNum]); // eslint-disable-line react-hooks/exhaustive-deps
  const prevKey = `${prevMonth.y}-${String(prevMonth.m).padStart(2, '0')}`;
  const nextKey = `${nextMonth.y}-${String(nextMonth.m).padStart(2, '0')}`;
  const prevCount = adjCounts[prevKey];
  const nextCount = adjCounts[nextKey];

  // Portal link for this client
  const clientSlug = activeClient?.slug || '';
  const portalToken = activeClient?.portal_token || '';
  const portalUrl = portalToken
    ? (clientSlug
        ? `${window.location.origin}/portal/${clientSlug}/${portalToken}`
        : `${window.location.origin}/portal/${portalToken}`)
    : null;

  const loadPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await postsApi.getAll(monthNum, yearNum);
      // Safe sort with date validation
      data.sort((a, b) => {
        const dateA = new Date(a.scheduled_date);
        const dateB = new Date(b.scheduled_date);
        const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
        const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
        return timeA - timeB;
      });
      setPosts(data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load posts", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [monthNum, yearNum, toast, activeClient]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Deep link: /month/:y/:m?post=<id> opens that post
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('post');
    if (!openId || isClientView || isModalOpen) return;
    const target = posts.find(p => p.id === openId);
    if (target) {
      setSelectedPost(target);
      setIsModalOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [posts, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const [dragTargetDate, setDragTargetDate] = useState(null);

  const handleDragEnd = async (result) => {
    if (!result.destination || isClientView) return;
    const { source, destination, draggableId } = result;
    
    // If dropped on a date target (droppableId starts with "day-")
    if (destination.droppableId.startsWith('day-')) {
      const newDateStr = destination.droppableId.replace('day-', '');
      const post = posts.find(p => p.id === draggableId);
      if (!post) return;
      
      // Check if already on this date
      const currentDate = post.scheduled_date.substring(0, 10);
      if (currentDate === newDateStr) {
        setDragTargetDate(null);
        return;
      }
      
      // Preserve the time portion, just update the date
      try {
        const oldDate = new Date(post.scheduled_date);
        const [y, m, d] = newDateStr.split('-').map(Number);
        oldDate.setFullYear(y, m - 1, d);
        
        await postsApi.update(post.id, { 
          scheduled_date: oldDate.toISOString() 
        });
        toast({ title: `Moved to ${format(new Date(newDateStr + 'T12:00:00'), 'MMM d')}` });
        loadPosts();
      } catch {
        toast({ title: "Failed to reschedule", variant: "destructive" });
      }
    } else {
      // Reorder within the same list
      const items = Array.from(posts);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setPosts(items);
    }
    setDragTargetDate(null);
  };

  const handleDragStart = () => {
    // Show date targets via CSS transition (no layout shift — strip is always mounted)
    setDragTargetDate('active');
  };

  const handlePostClick = (post) => {
    if (isClientView) {
      setPreviewPost(post);
      return;
    }
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleAddPost = () => {
    if (isClientView) return;
    setSelectedPost(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  const handlePostSave = (savedPost, wasDeleted) => {
    if (wasDeleted) {
      setPosts(posts.filter(p => p.id !== selectedPost?.id));
    } else if (selectedPost) {
      setPosts(posts.map(p => p.id === savedPost.id ? savedPost : p));
    } else {
      loadPosts();
    }
  };

  const handleDuplicate = (post) => {
    setDuplicatePost(post);
    setDuplicateDate(post.scheduled_date ? post.scheduled_date.split('T')[0] : '');
  };

  const confirmDuplicate = async () => {
    if (!duplicatePost) return;
    try {
      const dateStr = duplicateDate ? `${duplicateDate}T10:00:00` : null;
      await postsApi.duplicate(duplicatePost.id, dateStr);
      toast({ title: "Post duplicated!", description: "A copy has been created as a draft." });
      setDuplicatePost(null);
      loadPosts();
    } catch (error) {
      toast({ title: "Failed to duplicate", variant: "destructive" });
    }
  };

  const handleExportMonthlyPdf = async () => {
    setExportingPdf(true);
    try {
      const clientSlug = localStorage.getItem('activeClientSlug') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/monthly-pdf?year=${yearNum}&month=${monthNum}`;
      const res = await authFetch(url, {
        headers: { 'X-Client-Id': clientSlug },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${activeClient?.name?.replace(/\s+/g, '_') || monthName}_${monthName}_${yearNum}_Calendar.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast({ title: 'PDF exported', description: `${monthName} calendar downloaded` });
    } catch (err) {
      toast({ title: 'Export failed', description: err.message, variant: 'destructive' });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDeleteAllPosts = async () => {
    setDeleting(true);
    try {
      const clientSlug = localStorage.getItem('activeClientSlug') || '';
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/month/${yearNum}/${monthNum}`, {
        method: 'DELETE',
        headers: { 'X-Client-Id': clientSlug },
      });
      const data = await res.json();
      toast({ title: `Deleted ${data.deleted} posts`, description: `${monthName} ${yearNum} cleared` });
      setPosts([]);
      setDeleteAllOpen(false);
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectPost = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === posts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(posts.map(p => p.id)));
    }
  };

  const handleBulkWorkflow = async (workflowStatus) => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const clientSlug = localStorage.getItem('activeClientSlug') || '';
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/bulk-workflow`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Client-Id': clientSlug },
        body: JSON.stringify({ post_ids: Array.from(selectedIds), workflow_status: workflowStatus }),
      });
      const data = await res.json();
      const moved = data.updated - (data.held_for_approval || 0);
      toast({
        title: `${moved} post${moved !== 1 ? 's' : ''} moved to ${workflowStatus.replace('_', ' ')}`,
        description: data.held_for_approval ? `${data.held_for_approval} kept In Review until the client approves` : undefined,
      });
      setSelectedIds(new Set());
      setSelectMode(false);
      loadPosts();
    } catch (err) {
      toast({ title: 'Bulk update failed', description: err.message, variant: 'destructive' });
    } finally {
      setBulkLoading(false);
    }
  };


  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(monthDate),
    end: endOfMonth(monthDate)
  });

  const getPostsForDay = (day) => {
    return posts.filter(post => {
      try {
        return isSameDay(parseISO(post.scheduled_date), day);
      } catch {
        return false;
      }
    });
  };

  const defaultDate = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00Z`;

  return (
    <div className="min-h-screen bg-white" data-testid="month-detail-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-8">
          {/* Month Navigation Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to={backPath} className="p-2 rounded-md hover:bg-[#F8FAFC] transition-colors" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5 text-[#1a3a4a]/60" />
              </Link>
              <button
                onClick={goToPrevMonth}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-[#1a3a4a]/50 hover:text-[#1a3a4a] hover:bg-[#F8FAFC] transition-colors"
                data-testid="prev-month-btn"
                title={`Go to ${prevMonthName}`}
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{prevMonthName}</span>
                {prevCount > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#1a3a4a]/10 text-[#1a3a4a]/70" data-testid="prev-month-count">
                    {prevCount}
                  </span>
                )}
              </button>
              <div className="text-center">
                <h1 className="text-3xl sm:text-4xl font-black text-[#1a3a4a] tracking-tighter">
                  {monthName}
                </h1>
                <p className="text-[#1a3a4a]/60 text-sm mt-0.5">
                  {posts.length} {posts.length === 1 ? 'post' : 'posts'} planned
                  {isClientView && <span className="ml-2 text-[#2563eb]/60 text-sm font-medium">View Only</span>}
                </p>
              </div>
              <button
                onClick={goToNextMonth}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-[#1a3a4a]/50 hover:text-[#1a3a4a] hover:bg-[#F8FAFC] transition-colors"
                data-testid="next-month-btn"
                title={`Go to ${nextMonthName}`}
              >
                <span className="hidden sm:inline">{nextMonthName}</span>
                {nextCount > 0 && (
                  <span className="mr-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#1a3a4a]/10 text-[#1a3a4a]/70" data-testid="next-month-count">
                    {nextCount}
                  </span>
                )}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Portal Link */}
              {!isClientView && portalUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5 border-gray-200"
                  onClick={() => {
                    navigator.clipboard.writeText(portalUrl);
                    toast({ title: 'Portal link copied!' });
                  }}
                  data-testid="copy-portal-link-month-btn"
                  title="Copy client portal link"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  Portal Link
                </Button>
              )}
              {!isClientView && portalUrl && (
                <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="open-portal-month-btn">
                  <Button variant="outline" size="sm" className="text-xs gap-1.5 border-gray-200" title="Open portal in new tab">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </a>
              )}

              {/* Export Monthly PDF */}
              {!isClientView && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5 border-gray-200"
                  disabled={exportingPdf}
                  onClick={handleExportMonthlyPdf}
                  data-testid="export-monthly-pdf-btn"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  {exportingPdf ? 'Exporting...' : 'PDF'}
                </Button>
              )}

              {/* Delete All Posts */}
              {!isClientView && posts.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setDeleteAllOpen(true)}
                  data-testid="delete-all-posts-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Delete All</span>
                </Button>
              )}

              {/* Select Mode Toggle */}
              {!isClientView && posts.length > 1 && (
                <Button
                  variant={selectMode ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs gap-1.5 ${selectMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-gray-200'}`}
                  onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
                  data-testid="select-mode-btn"
                >
                  <CheckSquare className="w-3.5 h-3.5" />
                  {selectMode ? `${selectedIds.size} Selected` : 'Select'}
                </Button>
              )}

              {/* View Toggle */}
              <div className="flex items-center bg-[#F8FAFC] rounded-md p-1">
                <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`} data-testid="view-list-btn">
                  <List className={`w-4 h-4 ${viewMode === 'list' ? 'text-[#1a3a4a]' : 'text-[#1a3a4a]/40'}`} />
                </button>
                <button onClick={() => setViewMode('calendar')} className={`p-2 rounded ${viewMode === 'calendar' ? 'bg-white shadow-sm' : ''}`} data-testid="view-calendar-btn">
                  <Grid className={`w-4 h-4 ${viewMode === 'calendar' ? 'text-[#1a3a4a]' : 'text-[#1a3a4a]/40'}`} />
                </button>
              </div>

              {!isClientView && (
                <Button onClick={handleAddPost} className="btn-primary rounded-md" data-testid="add-post-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Post
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Loading */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-posts">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-[#c5d93d] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-[#1a3a4a]/60 text-sm font-mono uppercase tracking-wider">Loading posts...</p>
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-[#F8FAFC] rounded-md border border-dashed border-[#1a3a4a]/20" data-testid="empty-month">
            <Calendar className="w-12 h-12 text-[#1a3a4a]/20 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1a3a4a] mb-2">No posts for {monthName}</h3>
            <p className="text-[#1a3a4a]/60 mb-4">
              {isClientView ? 'No content scheduled yet.' : 'Start planning your content for this month'}
            </p>
            {!isClientView && (
              <Button onClick={handleAddPost} className="btn-primary rounded-md">
                <Plus className="w-4 h-4 mr-2" />
                Create First Post
              </Button>
            )}
          </div>
        ) : viewMode === 'list' ? (
          /* List View with Drag-to-Reschedule */
          <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            {/* Date Target Strip — always mounted, CSS-toggled to avoid layout shift during drag */}
            <div
              className={`mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl transition-all duration-200 ${
                dragTargetDate
                  ? 'opacity-100 max-h-[300px]'
                  : 'opacity-0 max-h-0 overflow-hidden p-0 m-0 border-0'
              }`}
              data-testid="drag-date-strip"
            >
              <p className="text-xs font-mono uppercase tracking-wider text-blue-600 mb-2">Drop on a date to reschedule</p>
              <div className="flex flex-wrap gap-1.5">
                {daysInMonth.map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const dayNum = day.getDate();
                  const isToday = isSameDay(day, new Date());
                  const dayPosts = getPostsForDay(day);
                  return (
                    <Droppable key={`day-${dateStr}`} droppableId={`day-${dateStr}`}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center text-xs font-medium transition-all cursor-default
                            ${snapshot.isDraggingOver 
                              ? 'bg-blue-500 text-white scale-110 shadow-lg ring-2 ring-blue-300' 
                              : isToday 
                                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300'
                            }`}
                          data-testid={`date-target-${dateStr}`}
                        >
                          <span className="leading-none">{dayNum}</span>
                          {dayPosts.length > 0 && !snapshot.isDraggingOver && (
                            <span className="w-1 h-1 rounded-full bg-blue-400 mt-0.5" />
                          )}
                          <div className="hidden">{provided.placeholder}</div>
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </div>
            <Droppable droppableId="posts">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`space-y-3 ${snapshot.isDraggingOver ? 'drop-target rounded-md p-4' : ''}`}
                  data-testid="posts-list"
                >
                  {posts.map((post, index) => (
                    <Draggable key={post.id} draggableId={post.id} index={index} isDragDisabled={isClientView || selectMode}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} className={`${snapshot.isDragging ? 'z-50' : ''} ${selectMode ? 'flex items-start gap-3' : ''}`}>
                          {selectMode && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleSelectPost(post.id); }}
                              className="mt-4 shrink-0"
                              data-testid={`select-post-${post.id}`}
                            >
                              {selectedIds.has(post.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-300 hover:text-gray-500" />
                              )}
                            </button>
                          )}
                          <div className={selectMode ? 'flex-1' : ''}>
                            <PostCard
                              post={post}
                              onClick={() => selectMode ? toggleSelectPost(post.id) : handlePostClick(post)}
                              dragHandleProps={isClientView || selectMode ? null : provided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                              onDuplicate={isClientView ? null : handleDuplicate}
                              readOnly={isClientView}
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          /* Calendar View with Color Coding */
          <div className="bg-[#F8FAFC] rounded-md p-4" data-testid="calendar-view">
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-4 px-1">
              {Object.entries(WORKFLOW_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs text-[#64748b]">
                  <span className={`w-2.5 h-2.5 rounded-full ${WORKFLOW_COLORS[key].dot}`} />
                  {label}
                </div>
              ))}
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-mono uppercase tracking-wider text-[#2b7a8a] py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: startOfMonth(monthDate).getDay() }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[100px]"></div>
              ))}

              {daysInMonth.map(day => {
                const dayPosts = getPostsForDay(day);
                const isToday = isSameDay(day, new Date());

                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[100px] bg-white rounded-md border p-2 ${
                      isToday ? 'border-[#c5d93d] ring-1 ring-[#c5d93d]/30' : 'border-[#1a3a4a]/10'
                    }`}
                  >
                    <div className={`text-sm font-medium mb-2 ${isToday ? 'text-[#c5d93d]' : 'text-[#1a3a4a]/60'}`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayPosts.slice(0, 3).map(post => {
                        const ws = post.workflow_status || 'draft';
                        const wStyle = WORKFLOW_COLORS[ws] || WORKFLOW_COLORS.draft;
                        return (
                          <button
                            key={post.id}
                            onClick={() => handlePostClick(post)}
                            className={`w-full text-left text-xs p-1.5 rounded truncate transition-colors flex items-center gap-1.5 hover:opacity-80 cursor-pointer`}
                            style={{ backgroundColor: `${wStyle.dot.replace('bg-', '')}` }}
                            data-testid={`calendar-post-${post.id}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${wStyle.dot}`} />
                            <span className="truncate text-[#1a3a4a]">{post.title}</span>
                            {ws === 'scheduled' && post.scheduled_time && (
                              <Send className="w-2.5 h-2.5 flex-shrink-0 text-blue-500" title="Auto-publish enabled" />
                            )}
                            {ws === 'posted' && (
                              <span className="w-2.5 h-2.5 flex-shrink-0 text-green-500 text-[8px] font-bold">✓</span>
                            )}
                          </button>
                        );
                      })}
                      {dayPosts.length > 3 && (
                        <p className="text-xs text-[#1a3a4a]/50 text-center">+{dayPosts.length - 3} more</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Bulk Action Bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#1a3a4a] text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-3 flex-wrap justify-center" data-testid="bulk-action-bar">
          <button
            onClick={toggleSelectAll}
            className="text-xs font-medium text-white/70 hover:text-white transition-colors"
            data-testid="select-all-btn"
          >
            {selectedIds.size === posts.length ? 'Deselect All' : `Select All (${posts.length})`}
          </button>
          <div className="w-px h-6 bg-white/20" />
          <span className="text-sm font-semibold">{selectedIds.size} selected</span>
          <div className="w-px h-6 bg-white/20" />
          <Button
            size="sm"
            onClick={() => handleBulkWorkflow('scheduled')}
            disabled={bulkLoading}
            className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-8 px-3"
            data-testid="bulk-schedule-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            Schedule
          </Button>
          <Button
            size="sm"
            onClick={() => handleBulkWorkflow('in_review')}
            disabled={bulkLoading}
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-8 px-3"
            data-testid="bulk-review-btn"
          >
            Review
          </Button>
          <Button
            size="sm"
            onClick={() => handleBulkWorkflow('draft')}
            disabled={bulkLoading}
            className="bg-gray-500 hover:bg-gray-600 text-white text-xs h-8 px-3"
            data-testid="bulk-draft-btn"
          >
            Draft
          </Button>
          <div className="w-px h-6 bg-white/20" />
          <Button
            size="sm"
            onClick={async () => {
              if (!window.confirm(`Delete ${selectedIds.size} post${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
              setBulkLoading(true);
              try {
                const clientSlug = localStorage.getItem('activeClientSlug') || '';
                const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/bulk-delete`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-Client-Id': clientSlug },
                  body: JSON.stringify({ post_ids: Array.from(selectedIds) }),
                });
                const data = await res.json();
                toast({ title: `${data.deleted} post${data.deleted !== 1 ? 's' : ''} deleted` });
                setSelectedIds(new Set());
                setSelectMode(false);
                loadPosts();
              } catch (err) {
                toast({ title: 'Bulk delete failed', variant: 'destructive' });
              } finally {
                setBulkLoading(false);
              }
            }}
            disabled={bulkLoading}
            className="bg-red-500 hover:bg-red-600 text-white text-xs h-8 px-3"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Delete
          </Button>
        </div>
      )}

      {/* Post Modal (Agency only) */}
      {!isClientView && (
        <PostModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          post={selectedPost}
          onSave={handlePostSave}
          defaultDate={defaultDate}
        />
      )}

      {/* Duplicate Dialog */}
      <Dialog open={!!duplicatePost} onOpenChange={() => setDuplicatePost(null)}>
        <DialogContent className="sm:max-w-[400px]" aria-describedby="duplicate-desc">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1e293b] flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#2563eb]" />
              Duplicate Post
            </DialogTitle>
            <p id="duplicate-desc" className="text-sm text-[#64748b] mt-1">
              Create a copy of "{duplicatePost?.title}" with a new date.
            </p>
          </DialogHeader>
          <div className="py-4">
            <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">
              New Date
            </label>
            <Input
              type="date"
              value={duplicateDate}
              onChange={(e) => setDuplicateDate(e.target.value)}
              className="border-[#e2e8f0]"
              data-testid="duplicate-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatePost(null)} className="border-[#e2e8f0]">Cancel</Button>
            <Button onClick={confirmDuplicate} className="btn-primary" data-testid="confirm-duplicate-btn">
              <Copy className="w-4 h-4 mr-2" />
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Preview (for client view and agency preview) */}
      <PostPreview
        isOpen={!!previewPost}
        onClose={() => setPreviewPost(null)}
        post={previewPost}
      />

      {/* Delete All Confirmation */}
      <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete All {monthName} Posts?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will permanently delete all {posts.length} post{posts.length !== 1 ? 's' : ''} in {monthName} {yearNum} for {activeClient?.name}. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllOpen(false)} className="border-gray-200">Cancel</Button>
            <Button
              onClick={handleDeleteAllPosts}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-all-btn"
            >
              {deleting ? 'Deleting...' : `Delete ${posts.length} Posts`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
